import { useEffect, useState } from 'react';
import {
  LuChevronLeft,
  LuChevronRight,
  LuDownload,
  LuLink,
  LuLock,
  LuMaximize,
  LuMoveHorizontal,
  LuUsers,
  LuZoomIn,
  LuZoomOut,
} from 'react-icons/lu';
import { IconButton, NovaMark } from '../components/ui';
import type { ConnStatus } from '../lib/connection';
import type { FitMode } from './PageView';

export interface TopBarProps {
  title: string;
  page: number;
  pageCount: number;
  navDisabled: boolean;
  onGo: (page: number) => void;
  isTeacher: boolean;
  following: boolean;
  followLocked: boolean;
  onToggleFollow: () => void;
  fit: FitMode;
  onFit: (fit: FitMode) => void;
  zoom: number;
  onZoom: (zoom: number) => void;
  status: ConnStatus;
  onCopyLink: () => void;
  onExport: () => void;
  exporting: boolean;
  panelOpen: boolean;
  onTogglePanel: () => void;
  onlineStudents: number;
  raisedHands: number;
}

const STATUS_TEXT: Record<ConnStatus, string> = {
  connecting: 'Bağlanıyor',
  online: 'Canlı',
  offline: 'Yeniden bağlanıyor',
  fatal: 'Bağlantı yok',
};

function PageInput({ page, pageCount, disabled, onGo }: { page: number; pageCount: number; disabled: boolean; onGo: (p: number) => void }) {
  const [value, setValue] = useState(String(page));
  useEffect(() => setValue(String(page)), [page]);
  const submit = () => {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 1 && n <= pageCount) onGo(Math.round(n));
    else setValue(String(page));
  };
  return (
    <span className="page-input">
      <input
        aria-label="Sayfa"
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={submit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      <span className="muted">/ {pageCount}</span>
    </span>
  );
}

export function TopBar(p: TopBarProps) {
  return (
    <header className="topbar">
      <div className="topbar-left">
        <a href="/" className="brand" title="Nova Class ana sayfa">
          <NovaMark />
          <span className="brand-name">Nova Class</span>
        </a>
        <span className="lesson-title" title={p.title}>
          {p.title}
        </span>
      </div>

      <div className="topbar-center">
        <IconButton label="Önceki sayfa (←)" onClick={() => p.onGo(p.page - 1)} disabled={p.navDisabled || p.page <= 1}>
          <LuChevronLeft />
        </IconButton>
        <PageInput page={p.page} pageCount={p.pageCount} disabled={p.navDisabled} onGo={p.onGo} />
        <IconButton label="Sonraki sayfa (→)" onClick={() => p.onGo(p.page + 1)} disabled={p.navDisabled || p.page >= p.pageCount}>
          <LuChevronRight />
        </IconButton>
        {!p.isTeacher && (
          <button
            type="button"
            className={`chip ${p.following ? 'chip-on' : ''}`}
            onClick={p.onToggleFollow}
            disabled={p.followLocked}
            title={p.followLocked ? 'Öğretmen sayfa takibini kilitledi' : 'Öğretmenin sayfasını takip et / serbest gezin'}
          >
            {p.followLocked && <LuLock />}
            {p.following ? 'Öğretmeni takip' : 'Serbest gezinme'}
          </button>
        )}
      </div>

      <div className="topbar-right">
        <div className="zoom-group">
          <IconButton label="Uzaklaştır" onClick={() => p.onZoom(Math.max(0.5, Math.round((p.zoom - 0.25) * 100) / 100))} disabled={p.zoom <= 0.5}>
            <LuZoomOut />
          </IconButton>
          <span className="zoom-value">{Math.round(p.zoom * 100)}%</span>
          <IconButton label="Yakınlaştır" onClick={() => p.onZoom(Math.min(3, Math.round((p.zoom + 0.25) * 100) / 100))} disabled={p.zoom >= 3}>
            <LuZoomIn />
          </IconButton>
          <IconButton
            label={p.fit === 'page' ? 'Genişliğe sığdır' : 'Sayfaya sığdır'}
            onClick={() => {
              p.onFit(p.fit === 'page' ? 'width' : 'page');
              p.onZoom(1);
            }}
          >
            {p.fit === 'page' ? <LuMoveHorizontal /> : <LuMaximize />}
          </IconButton>
        </div>
        <span className={`status status-${p.status}`} title={STATUS_TEXT[p.status]}>
          <span className="status-dot" />
          <span className="status-text">{STATUS_TEXT[p.status]}</span>
        </span>
        {p.isTeacher && (
          <>
            <button type="button" className="btn" onClick={p.onCopyLink} title="Öğrenci bağlantısını kopyala">
              <LuLink />
              <span className="btn-label">Öğrenci bağlantısı</span>
            </button>
            <button type="button" className="btn" onClick={p.onExport} disabled={p.exporting} title="Çizimleri PDF'ye işleyip indir">
              <LuDownload />
              <span className="btn-label">{p.exporting ? 'Hazırlanıyor…' : 'PDF indir'}</span>
            </button>
          </>
        )}
        <button type="button" className={`btn ${p.panelOpen ? 'btn-on' : ''}`} onClick={p.onTogglePanel} title="Katılımcılar">
          <LuUsers />
          <span>{p.onlineStudents}</span>
          {p.raisedHands > 0 && <span className="hand-badge">✋ {p.raisedHands}</span>}
        </button>
      </div>
    </header>
  );
}
